import React, { createContext, ReactNode, useContext, useEffect } from "react";
import { LanguageProps } from "@lib/types/Apps";
import { useTypedDispatch, useTypedSelector } from "@lib/redux";
import { setLocale } from "@lib/redux/slices/settings";
import dayjs from "dayjs";
import { getDefaultLanguage } from "@lib/helpers/Common";

export interface LocaleContextData {
  locale: LanguageProps;
  rtlLocale: string[];
}

export interface LocaleActionsData {
  updateLocale: (locale: LanguageProps) => void;
}

export const LocaleContext = createContext<LocaleContextData>({
  locale: getDefaultLanguage(),
  rtlLocale: ["ar"],
});
export const LocaleActionsContext = createContext<LocaleActionsData>({
  updateLocale: () => {},
});

export const useLocaleContext = () => useContext(LocaleContext);

export const useLocaleActionsContext = () => useContext(LocaleActionsContext);

interface LocaleContextProviderProps {
  children: ReactNode;
}

const LocaleContextProvider: React.FC<LocaleContextProviderProps> = ({
  children,
}) => {
  const dispatch = useTypedDispatch();
  const { locale } = useTypedSelector((state) => state.settings);

  const updateLocale = (locale: LanguageProps) => {
    dayjs.locale(locale.locale);
    dispatch(setLocale(locale));
  };

  useEffect(() => {
    dayjs.locale(locale.locale);
  }, [locale]);

  return (
    <LocaleContext.Provider
      value={{
        locale,
        rtlLocale: ["ar"],
      }}
    >
      <LocaleActionsContext.Provider
        value={{
          updateLocale,
        }}
      >
        {children}
      </LocaleActionsContext.Provider>
    </LocaleContext.Provider>
  );
};

export default LocaleContextProvider;
